import React from 'react';
// Use React Icons for web
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - react-icons types are available at build time
import * as Io from 'react-icons/io5';
import { IconConfig, iconRegistry } from './iconRegistry';

interface HybridIconProps {
  name: string;
  size?: number;
  color?: string;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Enhanced hybrid icon component with multi-tier fallback strategy:
 * 1. Native: Use Ionicons
 * 2. Web: Try Ionicons, fallback to emoji, then text
 * Ensures reliable icon display across all deployment environments
 */
export function HybridIcon({ name, size = 24, color = 'currentColor', style, className }: HybridIconProps) {
  const iconConfig: IconConfig | undefined = iconRegistry[name];
  
  // Warn in development if icon is not found
  if (import.meta.env.DEV && !iconConfig) {
    console.warn(`HybridIcon: Icon "${name}" not found in registry`);
  }
  
  const toReactIconName = (ionName: string) => {
    // kebab-case to PascalCase, keep 'Outline' suffix for -outline
    const outline = ionName.endsWith('-outline');
    const base = outline ? ionName.replace('-outline', '') : ionName;
    const pascal = base
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join('');
    return `Io${pascal}${outline ? 'Outline' : ''}`;
  };

  const ionName = iconConfig?.ionicon || name;
  const reactIconName = toReactIconName(ionName);
  const ReactIcon = (Io as Record<string, React.ComponentType<{ size: number; color: string; className?: string; style?: React.CSSProperties }>>)[reactIconName as string];

  if (ReactIcon) {
    return <ReactIcon size={size} color={color} style={style} className={className} />;
  }

  // Final fallback for unknown icons
  const fallbackIcon = iconConfig?.emoji || iconConfig?.text || '?';
  return (
    <span
      className={className}
      style={{
        fontSize: size,
        color: color,
        display: 'inline-block',
        textAlign: 'center',
        lineHeight: 1,
        width: size,
        height: size,
        ...style,
      }}
    >
      {fallbackIcon}
    </span>
  );
}
